/* This is a large button on mobile with any two icons at start and end. With any txext in between*/
import { FC, ReactNode } from 'react';
import styled from 'styled-components';
import {
  alignItems,
  border,
  borderRadius,
  boxShadow,
  color,
  display,
  height,
  justifyContent,
  maxWidth,
  minWidth,
  space,
  width,
} from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { StyledSystemProps } from '../../theme/StyledSystemProps';
import { Flex } from '.';
import { device } from '../../utils/device';

interface ButtonProps extends StyledSystemProps {
  id?: string;
  text?: string;
  secondaryText?: string;
  size?: string;
  color?: string;
  hover?: boolean;
  isIcon?: boolean;
  minimal?: boolean;
  outline?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
  appendIcon?: ReactNode;
  preppendIcon?: ReactNode;
  style?: object;
  type: 'button' | 'submit' | 'reset';
  isWeb?: boolean;
}

const StyledButton = styled.button`
  ${space};
  ${width};
  ${color};
  ${border};
  ${height};
  ${(props: ButtonProps) => (props.hidden ? 'none' : display)};
  ${maxWidth};
  ${alignItems};
  ${borderRadius};
  ${justifyContent};
  ${(props: ButtonProps) => !props.isIcon && minWidth};
  ${(props: ButtonProps) => !props.minimal && boxShadow};
  cursor: pointer;
  &:hover {
    box-shadow: ${(props: ButtonProps) =>
      props.bg === 'transparent' || props.minimal ? 'none' : '0 1px 4px rgba(50,50,93,0.4)'};
    background: ${(props: ButtonProps) => props.minimal && 'rgba(50,50,93,0.05)'};
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    transform: ${(props: ButtonProps) => props.hover && 'translateY(-1px)'};
    cursor: pointer;
  }
  &:disabled {
    opacity: 0.6;
  }

  i,
  i > svg {
    height: ${themeGet('space[2]', '30px')};
    width: ${themeGet('space[2]', '30px')};
    margin-left: 5px;
  }
  @media ${device.mobileS} {
    .primaryText {
      font-weight: ${(props: ButtonProps) => !props.isWeb && 600};
      font-size: ${(props: ButtonProps) => !props.isWeb && '1em'};
      margin: 10px 0px 0px 20px;
    }
    .secondaryText {
      font-weight: ${(props: ButtonProps) => !props.isWeb && 500};
      font-size: ${(props: ButtonProps) => !props.isWeb && '0.8em'};
      margin: 10px 0px 10px 20px;
    }
  }
  @media ${device.mobileM} {
    .primaryText {
      font-weight: ${(props: ButtonProps) => !props.isWeb && 600};
      font-size: ${(props: ButtonProps) => !props.isWeb && '1.2em'};
      margin: 10px 0px 0px 20px;
    }
    .secondaryText {
      font-weight: ${(props: ButtonProps) => !props.isWeb && 500};
      font-size: ${(props: ButtonProps) => !props.isWeb && '1em'};
      margin: 10px 0px 10px 20px;
    }
  }
  @media ${device.mobileL} {
    .primaryText {
      font-weight: ${(props: ButtonProps) => !props.isWeb && 600};
      font-size: ${(props: ButtonProps) => !props.isWeb && '1.5em'};
      margin: 10px 0px 0px 20px;
    }
    .secondaryText {
      font-weight: ${(props: ButtonProps) => !props.isWeb && 500};
      font-size: ${(props: ButtonProps) => !props.isWeb && '1.3em'};
      margin: 10px 0px 10px 20px;
    }
  }
  @media ${device.tablet} {
    .primaryText {
      font-weight: ${(props: ButtonProps) => !props.isWeb && 600};
      font-size: ${(props: ButtonProps) => !props.isWeb && '1.8em'};
      margin: 10px 0px 0px 20px;
    }
    .secondaryText {
      font-weight: ${(props: ButtonProps) => !props.isWeb && 500};
      font-size: ${(props: ButtonProps) => !props.isWeb && '1.6em'};
      margin: 10px 0px 10px 20px;
    }
  }
`;

const Button: FC<ButtonProps> = props => {
  return (
    <StyledButton disabled={props.isLoading} {...props} justifyContent="space-between">
      {props.preppendIcon && <i className="preppendIcon">{props.preppendIcon}</i>}
      <Flex size="1" direction="column" alignItems="flex-start">
        <p className="primaryText">{props.text}</p>
        <p className="secondaryText">{props.secondaryText}</p>
      </Flex>
      {props.appendIcon && <i className="appendIcon">{props.appendIcon}</i>}
    </StyledButton>
  );
};

Button.defaultProps = {
  alignItems: 'center',
  preppendIcon: null,
  appendIcon: null,
  size: 'md',
  bg: 'primary',
  color: '#FFF',
  display: 'flex',
  flex: '1',
  width: '100%',
  outline: false,
  height: '80px',
  minimal: false,
  text: 'Button',
  isLoading: false,
  minWidth: '150px',
  onClick: () => null,
  borderRadius: '4px',
  justifyContent: 'space-between',
  boxShadow: '0 3px 10px rgba(50,50,93,0.2)',
};

export default Button;
